"use client"

import { useCallback, useRef, useState } from "react"
import { radios } from "@/data/radio/index"
import {
  UseMusicRadioProps,
  RadioProgram,
  RadioMeta,
} from "./musicTypes"

export const useMusicRadio = ({
  audioRef,
  startTrack,
  resume,
  stop,
  insightsTrackRef,
  insightsPlayingRef,
  updateCurrentTrackData,
}: UseMusicRadioProps) => {
  const [activeRadio, setActiveRadio] = useState<string | null>(null)
  const [isRadioPlaying, setIsRadioPlaying] = useState(false)
  const [isRadioLoading, setIsRadioLoading] = useState(false)

  const radioPlayRequestId = useRef(0)
  const radioHasStartedRef = useRef(false)

  const getTodayRadio = useCallback(
    (id: string): RadioProgram[] => {
      const radio = radios[id as keyof typeof radios]
      if (!radio) return []

      const days = [
        "domingo",
        "lunes",
        "martes",
        "miercoles",
        "jueves",
        "viernes",
        "sabado",
      ] as const

      return (radio[days[new Date().getDay()]] ?? []) as RadioProgram[]
    },
    [],
  )

  const getRadioMeta = useCallback(
    (id: string): RadioMeta => {
      const radioMap = {
        bass: { name: "Bass", color: "#caff3d" },
        brazilian: { name: "Brazilian", color: "#ff6600" },
        pop: { name: "Pop", color: "#ff4fd8" },
        reggaeton: { name: "Reggaeton", color: "#ff3b30" },
        world: { name: "World", color: "#4da6ff" },
      } as const

      return (
        radioMap[id as keyof typeof radioMap] ?? {
          name: id,
          color: "#ffffff",
        }
      )
    },
    [],
  )

  const getCurrentProgram = useCallback(
    (programs: RadioProgram[]): RadioProgram | null => {
      const now = new Date()
      const current =
        now.getHours() * 3600 +
        now.getMinutes() * 60 +
        now.getSeconds()

      return (
        programs.find((program) => {
          if (!program?.startTime || !program?.endTime) return false

          const [sh, sm] = program.startTime.split(":").map(Number)
          const [eh, em] = program.endTime.split(":").map(Number)

          const start = sh * 3600 + sm * 60
          const end = eh * 3600 + em * 60

          if (end > start) {
            return current >= start && current < end
          }

          return current >= start || current < end
        }) ?? null
      )
    },
    [],
  )

  const getProgramOffset = useCallback(
    (program: RadioProgram) => {
      const now = new Date()

      const [sh, sm] = program.startTime.split(":").map(Number)
      const [eh, em] = program.endTime.split(":").map(Number)

      const start = sh * 3600 + sm * 60
      const end = eh * 3600 + em * 60

      const current =
        now.getHours() * 3600 +
        now.getMinutes() * 60 +
        now.getSeconds()

      if (end > start) return Math.max(0, current - start)
      if (current >= start) return current - start

      return 86400 - start + current
    },
    [],
  )

  const stopRadioStream = useCallback(() => {
    const audio = audioRef.current

    radioPlayRequestId.current++
    radioHasStartedRef.current = false

    if (insightsPlayingRef.current) {
      insightsPlayingRef.current = false
    }

    stop()
    insightsTrackRef.current = null

    if (audio) {
      audio.pause()

      audio.onloadedmetadata = null
      audio.oncanplay = null
      audio.onplaying = null
      audio.onerror = null
      audio.onended = null
      audio.onwaiting = null
      audio.onstalled = null
      audio.onabort = null

      audio.removeAttribute("src")
      audio.load()
    }

    setIsRadioLoading(false)
    setIsRadioPlaying(false)
  }, [
    audioRef,
    stop,
    insightsPlayingRef,
    insightsTrackRef,
  ])

  const playRadioStream = useCallback(
    async (id: string) => {
      /*
       * =====================================================
       * 1. RESPUESTA VISUAL INMEDIATA
       * =====================================================
       *
       * Esto debe ser lo PRIMERO que ocurre.
       * No await, no audio, no búsqueda de programa antes.
       */
      setActiveRadio(id)
      setIsRadioPlaying(false)
      setIsRadioLoading(true)

      /*
       * =====================================================
       * 2. CREAR ID DE PETICIÓN INMEDIATAMENTE
       * =====================================================
       */
      const requestId = ++radioPlayRequestId.current
      radioHasStartedRef.current = false

      /*
       * Dejamos un frame para que React pinte el loader.
       * Después continuamos con el audio.
       */
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve())
      })

      if (requestId !== radioPlayRequestId.current) return

      /*
       * =====================================================
       * 3. AUDIO
       * =====================================================
       */
      const audio = audioRef.current

      if (!audio) {
        setIsRadioLoading(false)
        setIsRadioPlaying(false)
        return
      }

      /*
       * =====================================================
       * 4. BUSCAR PROGRAMA ACTUAL
       * =====================================================
       */
      const programs = getTodayRadio(id)
      const current = getCurrentProgram(programs)

      if (!current) {
        stopRadioStream()
        return
      }

      const src = Array.isArray(current.src)
        ? current.src[0]
        : current.src

      if (!src) {
        stopRadioStream()
        return
      }

      const offset = getProgramOffset(current)

      if (requestId !== radioPlayRequestId.current) return

      /*
       * =====================================================
       * 5. LIMPIAR AUDIO ANTERIOR
       * =====================================================
       */
      audio.pause()

      audio.onloadedmetadata = null
      audio.oncanplay = null
      audio.onplaying = null
      audio.onerror = null
      audio.onended = null
      audio.onwaiting = null
      audio.onstalled = null
      audio.onabort = null

      audio.removeAttribute("src")
      audio.load()

      /*
       * =====================================================
       * 6. METADATA
       * =====================================================
       */
      const radioMeta = getRadioMeta(id)

      updateCurrentTrackData({
        type: "radio",
        radioId: id,
        radioName: radioMeta.name,
        radioColor: radioMeta.color,
        track: src,
        trackImg: current.cover,
        trackName: current.title,
        trackArtist: `Mixtape ${radioMeta.name}`,
        startTime: current.startTime,
        endTime: current.endTime,
        _ts: Date.now(),
      })

      audio.src = src

      /*
       * =====================================================
       * 7. METADATA CARGADA
       * =====================================================
       */
      audio.onloadedmetadata = async () => {
        if (requestId !== radioPlayRequestId.current) return
      
        const duration = audio.duration
      
        if (
          Number.isFinite(duration) &&
          duration > 0 &&
          offset >= duration
        ) {
          audio.currentTime = 0
        } else if (
          offset >= 0 &&
          Number.isFinite(offset) &&
          (
            !Number.isFinite(duration) ||
            duration <= 0 ||
            offset < duration
          )
        ) {
          audio.currentTime = offset
        } else {
          audio.currentTime = 0
        }
      
        try {
          await audio.play()
        
          if (requestId !== radioPlayRequestId.current) {
            audio.pause()
          }
        } catch (error: any) {
          if (requestId !== radioPlayRequestId.current) return
        
          setIsRadioLoading(false)
          setIsRadioPlaying(false)
          radioHasStartedRef.current = false
        
          if (error?.name !== "AbortError") {
            console.error("[RADIO PLAY ERROR]", error)
          }
        }
      }
      
      /* audio.onloadedmetadata = async () => {
        if (requestId !== radioPlayRequestId.current) return

        const duration = audio.duration

        if (
          Number.isFinite(duration) &&
          duration > 0 &&
          offset >= duration
        ) {
          audio.pause()
          audio.currentTime = 0
          setIsRadioPlaying(true)
          setIsRadioLoading(false)
          return
        }

        if (
          offset < 0 ||
          !Number.isFinite(offset) ||
          (
            Number.isFinite(duration) &&
            duration > 0 &&
            offset >= duration
          )
        ) {
          audio.pause()
          setIsRadioPlaying(true)
          setIsRadioLoading(false)
          return
        }

        try {
          audio.currentTime = offset

          await audio.play()

          if (requestId !== radioPlayRequestId.current) {
            audio.pause()
          }
        } catch (error: any) {
          if (requestId !== radioPlayRequestId.current) return

          setIsRadioLoading(false)
          setIsRadioPlaying(false)

          if (error?.name !== "AbortError") {
            console.error("[RADIO PLAY ERROR]", error)
          }
        }
      } */

      /*
       * =====================================================
       * 8. AUDIO REALMENTE REPRODUCIENDO
       * =====================================================
       */
      audio.onplaying = () => {
        if (requestId !== radioPlayRequestId.current) return

        const wasTracking = insightsPlayingRef.current

        radioHasStartedRef.current = true
        insightsPlayingRef.current = true

        setIsRadioLoading(false)
        setIsRadioPlaying(true)

        if (!insightsTrackRef.current) {
          startTrack({
            id: `radio:${id}`,
            title: current.title || radioMeta.name,
            artist: `Mixtape ${radioMeta.name}`,
            image: current.cover,
            duration: 0,
            radioId: id,
            radioName: radioMeta.name,
            radioColor: radioMeta.color,
          })
        }

        if (wasTracking) return

        resume()
      }

      /*
       * =====================================================
       * 9. BUFFERING
       * =====================================================
       */
      audio.onwaiting = () => {
        if (requestId !== radioPlayRequestId.current) return

        if (!radioHasStartedRef.current) {
          setIsRadioLoading(true)
        }
      }

      audio.onstalled = () => {
        if (requestId !== radioPlayRequestId.current) return

        if (!radioHasStartedRef.current) {
          setIsRadioLoading(true)
        }
      }

      /*
       * =====================================================
       * 10. ABORT
       * =====================================================
       */
      audio.onabort = () => {
        if (requestId !== radioPlayRequestId.current) return

        setIsRadioLoading(false)
        setIsRadioPlaying(false)
      }

      /*
       * =====================================================
       * 11. ERROR
       * =====================================================
       */
      audio.onerror = () => {
        if (requestId !== radioPlayRequestId.current) return

        radioHasStartedRef.current = false

        setIsRadioLoading(false)
        setIsRadioPlaying(false)

        console.error(
          "[RADIO] Error cargando:",
          src,
        )
      }

      /*
       * =====================================================
       * 12. STREAM TERMINADO
       * =====================================================
       */
      audio.onended = () => {
        if (requestId !== radioPlayRequestId.current) return

        audio.pause()

        setIsRadioLoading(false)

        /*
         * La radio sigue siendo la radio activa.
         */
        setIsRadioPlaying(true)
      }

      /*
       * =====================================================
       * 13. INICIAR CARGA
       * =====================================================
       */
      audio.load()
    },
    [
      audioRef,
      getTodayRadio,
      getCurrentProgram,
      getProgramOffset,
      stopRadioStream,
      getRadioMeta,
      updateCurrentTrackData,
      startTrack,
      resume,
      insightsTrackRef,
      insightsPlayingRef,
    ],
  )

  return {
    getTodayRadio,
    getRadioMeta,
    getCurrentProgram,
    getProgramOffset,
    playRadioStream,
    stopRadioStream,
    activeRadio,
    isRadioPlaying,
    isRadioLoading,
    radioPlayRequestId,
    radioHasStartedRef,
  }
}